/* eslint-disable no-console */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { login } from '../services/api.js';
import { useAuth } from '../context/AuthContext.js';
import {
  isValidEmail,
  isValidPassword,
  getPasswordErrorMessage,
} from '../utils/validation.js';
import ReatorBg from '../assets/reator.png';

const SignInPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { completeLogin } = useAuth();

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const navigateByRole = roleCode => {
    if (roleCode === 'ADM') {
      navigate('/dashboard');
    } else if (roleCode === 'COR' || roleCode === 'PRO') {
      navigate('/');
    } else if (roleCode === 'MON') {
      navigate('/monitor-dashboard');
    } else if (roleCode === 'MBR') {
      navigate('/cursos');
    } else {
      navigate('/');
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!isValidEmail(formData.email)) {
      setError('Por favor, insira um e-mail válido.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data = await login(formData.email, formData.password);

      if (data.access_token) {
        // Access token in memory only; refresh must be HttpOnly cookie from backend.
        // Ignore data.refresh_token if still returned (compat) — never persist in JS.
        try {
          const userData = await completeLogin(data.access_token);
          navigateByRole(userData?.cd_role);
        } catch (userErr) {
          console.error('Error fetching user data:', userErr);
          navigate('/');
        }
      } else {
        throw new Error('Token not received from server');
      }
    } catch (err) {
      const errorMsg = err?.response?.data?.detail || err?.message || '';
      if (typeof errorMsg === 'string' && errorMsg.includes('Conta não confirmada')) {
        setError('Conta não confirmada. Verifique seu email para confirmar o cadastro.');
      } else {
        setError('Email ou senha incorretos.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='min-h-screen flex font-sans text-gray-800 bg-white'>
      <div className='hidden md:flex md:w-1/2 relative flex-col justify-center items-center text-white p-12 overflow-hidden'>
        <div
          className='absolute inset-0 z-0'
          style={{
            backgroundImage: `url(${ReatorBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className='absolute inset-0 bg-[#0a2e3d]/40 mix-blend-multiply' />
          <div className='absolute inset-0 bg-gradient-to-tr from-[#0a2e3d] via-transparent to-transparent opacity-40' />
        </div>

        <div className='relative z-10 text-center'>
          <h1 className='text-4xl md:text-5xl font-bold mb-6 leading-tight'>
            Bem-vindo de volta!
          </h1>
          <p className='text-lg md:text-xl text-gray-200 max-w-md mx-auto'>
            Acesse sua conta para continuar experenciando nossos conteúdos.
          </p>
        </div>
      </div>

      <div className='w-full md:w-1/2 flex items-center justify-center p-8 md:p-12 relative'>
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className='w-full max-w-md'
        >
          <div className='mb-8'>
            <h2 className='text-3xl font-bold text-[#0a2e3d] mb-2'>Login</h2>
            <p className='text-gray-500'>
              Entre com suas credenciais para acessar
            </p>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className='mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm flex items-center shadow-sm'
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-5 w-5 mr-2 flex-shrink-0'
                viewBox='0 0 20 20'
                fill='currentColor'
              >
                <path
                  fillRule='evenodd'
                  d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z'
                  clipRule='evenodd'
                />
              </svg>
              {error}
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className='space-y-6'>
            <div>
              <label
                htmlFor='email'
                className='block text-sm font-semibold text-gray-700 mb-2'
              >
                E-mail
              </label>
              <input
                type='email'
                id='email'
                name='email'
                value={formData.email}
                onChange={handleChange}
                required
                className='w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200'
                placeholder='seu@email.com'
              />
            </div>

            <div>
              <div className='flex justify-between items-center mb-2'>
                <label
                  htmlFor='password'
                  className='block text-sm font-semibold text-gray-700'
                >
                  Senha
                </label>
                <Link
                  to='/forgot-password'
                  className='text-sm text-cyan-600 hover:text-cyan-800 font-medium transition-colors'
                >
                  Esqueceu a senha?
                </Link>
              </div>
              <input
                type='password'
                id='password'
                name='password'
                value={formData.password}
                onChange={handleChange}
                required
                className='w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200'
                placeholder='••••••••'
              />
            </div>

            <motion.button
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              type='submit'
              disabled={loading}
              className='w-full bg-[#0a2e3d] hover:bg-[#041c26] text-white font-bold py-3.5 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center text-lg'
            >
              {loading ? (
                <>
                  <svg
                    className='animate-spin -ml-1 mr-3 h-5 w-5 text-white'
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                  >
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                    ></circle>
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    ></path>
                  </svg>
                  Entrando...
                </>
              ) : (
                'Entrar'
              )}
            </motion.button>
          </form>

          <div className='mt-8 text-center text-sm text-gray-600'>
            Não tem uma conta?{' '}
            <Link
              to='/signup'
              className='text-cyan-600 hover:text-cyan-800 font-bold ml-1 transition-colors'
            >
              Cadastre-se agora
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SignInPage;
